import { useCallback, useState, type ComponentProps } from "react";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { observer } from "mobx-react-lite";

import type { components } from "@/__openapi__/backend/schema";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { MDSButton, MDSLoadingSpinner, MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import { CommercialMeterKey, type CommercialPlanSnapshot } from "@/modules/commercial-plans";
import { api } from "@/modules/api";
import {
  SettingsIntegrationHeader,
  SettingsIntegrationPageWrapper,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";
import {
  SettingsUsagePageView,
  type CurrentUsageMeter,
  type MonthlyUsage,
  type UsageMetric,
} from "@/pages/settings/settings-usage-page.view";
import { useAppStore } from "@/store";

type UsageHistoryResponse = components["schemas"]["CoreApi__UsageHistoryResponseSchema"];
type UsagePageViewProps = ComponentProps<typeof SettingsUsagePageView>;

const formatVoiceDuration = (minutes: number): string => {
  const roundedMinutes = Math.round(minutes);
  const hours = Math.floor(roundedMinutes / 60);
  const remainingMinutes = roundedMinutes % 60;

  if (hours === 0) return `${remainingMinutes}m`;
  if (remainingMinutes === 0) return `${hours}h`;

  return `${hours}h ${remainingMinutes}m`;
};

const formatDateRange = (history: UsageHistoryResponse): string => {
  const startsAt = DateTime.fromISO(history.starts_at, { zone: "utc" });
  const endsAt = DateTime.fromISO(history.ends_at, { zone: "utc" });
  const trackingStartedAt = history.tracking_started_at
    ? DateTime.fromISO(history.tracking_started_at, { zone: "utc" })
    : null;

  if (trackingStartedAt && trackingStartedAt > startsAt) {
    return `Usage tracked since ${trackingStartedAt.toFormat("LLL d, yyyy")}`;
  }

  return `${startsAt.toFormat("LLL d")} – ${endsAt.toFormat("LLL d, yyyy")}`;
};

const buildCurrentPeriodLabel = (snapshot: CommercialPlanSnapshot | null): string => {
  if (!snapshot) return "Current billing period";

  const startsAt = DateTime.fromISO(snapshot.period.startsAt);
  const endsAt = DateTime.fromISO(snapshot.period.endsAt);
  const displayEnd = endsAt.minus({ days: 1 });
  const daysLeft = Math.max(Math.ceil(endsAt.diffNow("days").days), 0);

  return `${startsAt.toFormat("LLL d")} – ${displayEnd.toFormat("LLL d")} · ${daysLeft} ${
    daysLeft === 1 ? "day" : "days"
  } left`;
};

const buildCurrentUsageMeters = (snapshot: CommercialPlanSnapshot | null): CurrentUsageMeter[] => {
  if (!snapshot) return [];

  const meter = (
    key: keyof typeof CommercialMeterKey,
    label: string,
    unit?: string
  ): CurrentUsageMeter => {
    const usage = snapshot.meters[CommercialMeterKey[key]];
    return { label, used: usage.used, allowance: usage.allowance, unit };
  };

  return [
    meter("UserRequests", "Agent requests"),
    meter("NewNotes", "New notes"),
    meter("RoutineExecutions", "Routine runs"),
    meter("VoiceModeHours", "Voice mode", "hrs"),
  ];
};

export const buildUsagePageViewProps = ({
  history,
  snapshot,
}: {
  history: UsageHistoryResponse;
  snapshot: CommercialPlanSnapshot | null;
}): UsagePageViewProps => {
  const monthlyUsage: MonthlyUsage[] = history.months.map(month => ({
    label: DateTime.fromISO(month.month_started_at, { zone: "utc" }).toFormat("LLL"),
    values: {
      voice: Math.round(month.usage.voice_mode_hours * 60),
      notes: month.usage.new_notes,
      requests: month.usage.user_requests,
      routines: month.usage.routine_executions,
    },
  }));
  const totals = monthlyUsage.reduce(
    (sum, month) => ({
      voice: sum.voice + month.values.voice,
      notes: sum.notes + month.values.notes,
      requests: sum.requests + month.values.requests,
      routines: sum.routines + month.values.routines,
    }),
    { voice: 0, notes: 0, requests: 0, routines: 0 }
  );
  const metrics: UsageMetric[] = [
    { key: "voice", label: "Voice mode", total: formatVoiceDuration(totals.voice) },
    { key: "notes", label: "Notes created", total: totals.notes.toLocaleString() },
    { key: "requests", label: "Agent requests", total: totals.requests.toLocaleString() },
    { key: "routines", label: "Routine runs", total: totals.routines.toLocaleString() },
  ];

  return {
    year: history.year,
    dateRangeLabel: formatDateRange(history),
    planName: snapshot?.currentPlan.displayName ?? "Current plan",
    currentPeriodLabel: buildCurrentPeriodLabel(snapshot),
    currentUsageMeters: buildCurrentUsageMeters(snapshot),
    metrics,
    monthlyUsage,
  };
};

export const SettingsUsagePage = observer(() => {
  const { store } = useAppStore();
  const [history, setHistory] = useState<UsageHistoryResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const year = DateTime.utc().year;

  const loadUsage = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const response = await api.get("/v2/usage/history", {
        params: { query: { year } },
      });

      if (response.error || !response.data) {
        setErrorMessage("We couldn’t load your usage. Please try again.");
      } else {
        setHistory(response.data);
      }
    } catch {
      setErrorMessage("We couldn’t load your usage. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [year]);

  useEffectOnMount(() => {
    void loadUsage();
  });

  if (isLoading || !history) {
    return (
      <SettingsUsageStatus
        message={errorMessage}
        onRetry={errorMessage ? () => void loadUsage() : undefined}
      />
    );
  }

  return (
    <SettingsUsagePageView
      {...buildUsagePageViewProps({
        history,
        snapshot: store.platformInfo.commercialPlanSnapshot,
      })}
    />
  );
});

const SettingsUsageStatus = ({
  message,
  onRetry,
}: {
  message: string | null;
  onRetry?: () => void;
}) => (
  <SettingsIntegrationPageWrapper>
    <SettingsIntegrationHeader breadcrumb="Account" title="Usage" />
    <SettingsIntegrationScrollableArea>
      <SettingsIntegrationSection>
        <StatusContent>
          {message ? (
            <>
              <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
                {message}
              </MDSText>
              {onRetry && <MDSButton onPress={onRetry}>Try again</MDSButton>}
            </>
          ) : (
            <MDSLoadingSpinner size={20} />
          )}
        </StatusContent>
      </SettingsIntegrationSection>
    </SettingsIntegrationScrollableArea>
  </SettingsIntegrationPageWrapper>
);

const StatusContent = styled.div`
  display: flex;
  min-height: 240px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;
